import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Sparkles, ShieldAlert, Check, X, PauseCircle, Loader2, RefreshCw,
  MessageSquareText, BookOpen, AlertTriangle, ShieldCheck, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import LearningMetricsChart from "@/components/LearningMetricsChart";

/**
 * Admin Learning Queue — CAATF flywheel review surface.
 *
 * SECURITY: renders behind RequireAuth (App.js) AND additionally enforces
 * `role === "admin"` + `twofa_enabled` locally so a non-admin who somehow
 * lands on this URL sees a redirect, not the data.  Backend endpoints
 * (`require_admin_2fa`) provide the real enforcement.
 */
const CLINICAL_SAFETY_DOMAINS = ["red_flags", "medication_considerations"];

const DOMAIN_OPTIONS = [
  { value: "charting", label: "Charting / SOAP" },
  { value: "documentation", label: "Documentation" },
  { value: "prior_auth", label: "Prior Auth" },
  { value: "cardiology", label: "Cardiology" },
  { value: "eod", label: "End-of-Day" },
  { value: "red_flags", label: "🚨 Red Flags (safety)" },
  { value: "medication_considerations", label: "🚨 Medication Considerations (safety)" },
  { value: "billing", label: "Billing / Claims" },
  { value: "referrals", label: "Referrals" },
];

function parseDomainHint(text) {
  // AiAnswerCard prefixes correct_approach_text with "[domain:xyz] ..." when
  // the physician picks a domain tag.  Extract & strip.
  if (!text) return { domain: null, body: text || "" };
  const m = text.match(/^\[domain:([a-z_]+)\]\s*/i);
  if (!m) return { domain: null, body: text };
  return { domain: m[1], body: text.slice(m[0].length) };
}

export default function LearningQueue() {
  const u = auth.user() || {};
  const isAdmin = u.role === "admin" && u.twofa_enabled;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState("new");
  const [expanded, setExpanded] = useState(null);
  const [promoteState, setPromoteState] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (isAdmin) load(); }, [activeStatus, isAdmin]);

  if (!isAdmin) {
    // Non-admin or admin without 2FA — bounce back.  Backend is authoritative;
    // this is just to avoid rendering the shell for someone who can't use it.
    return <Navigate to="/dashboard" replace />;
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/learning/feedback?status=${activeStatus}&limit=100`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  }

  function onOpen(fb) {
    const hint = parseDomainHint(fb.correct_approach_text || "");
    setExpanded(fb.feedback_id);
    setPromoteState({
      [fb.feedback_id]: {
        domain: hint.domain || "charting",
        title: (fb.correction_text || "").slice(0, 120) || "Physician correction",
        content: hint.body || fb.correction_text || fb.correct_approach_text || "",
        typedVerify: "",
      },
    });
  }

  function updatePromote(feedbackId, patch) {
    setPromoteState((s) => ({ ...s, [feedbackId]: { ...s[feedbackId], ...patch } }));
  }

  async function promote(fb) {
    const state = promoteState[fb.feedback_id];
    if (!state) return;
    if (!state.title.trim() || !state.content.trim()) {
      toast.error("Title and content are required.");
      return;
    }
    if (CLINICAL_SAFETY_DOMAINS.includes(state.domain) && state.typedVerify !== "VERIFY") {
      toast.error(`Type VERIFY (uppercase) to promote into the ${state.domain} safety tier.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/learning/feedback/${fb.feedback_id}/promote`, {
        domain: state.domain,
        title: state.title.trim(),
        content: state.content.trim(),
      });
      toast.success(`Promoted → entry ${data.entry.entry_id.slice(0, 8)}… (${data.entry.confidence})`);
      setExpanded(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promote failed");
    } finally { setBusy(false); }
  }

  async function reject(fb) {
    if (!window.confirm("Reject this feedback? It stays logged but never enters the KB.")) return;
    setBusy(true);
    try {
      await api.post(`/learning/feedback/${fb.feedback_id}/reject`, {
        reason: "Reviewed and rejected",
      });
      toast.success("Feedback rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  }

  async function hold(fb) {
    setBusy(true);
    try {
      await api.post(`/learning/feedback/${fb.feedback_id}/hold`);
      toast.success("Feedback moved to Hold");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hold failed");
    } finally { setBusy(false); }
  }

  async function sendDigestNow() {
    if (!window.confirm("Send the Learning Queue digest email now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/learning-digest/send-now");
      toast.success(`Digest sent to ${data.recipients?.join(", ") || "recipients"}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Digest send failed");
      toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader
        eyebrow="CAATF Learning Layer"
        title="Feedback Review Queue"
        subtitle="Convert physician corrections into shared knowledge. Clinical-safety domains require typed VERIFY."
        testid="learning-queue-header"
      />

      <LearningMetricsChart testid="lq-metrics-chart" />

      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="lq-filters">
        {["new", "under_review", "promoted", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setActiveStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeStatus === s
                ? "bg-brand-primary text-white border-brand-primary"
                : "border-brand text-brand-muted hover:bg-brand-bg"
            }`}
            data-testid={`lq-filter-${s}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand text-xs text-brand-muted hover:bg-brand-bg"
          data-testid="lq-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <button
          onClick={sendDigestNow}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-primary text-xs text-brand-primary hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-60"
          data-testid="lq-send-digest"
          title="Send the weekly digest email to configured admins now"
        >
          <Mail className="w-3.5 h-3.5" /> Send digest now
        </button>
      </div>

      <SectionCard
        title={`Feedback (${total})`}
        subtitle={loading ? "Loading…" : `${items.length} shown`}
        testid="lq-card"
      >
        {loading && (
          <div className="flex items-center gap-2 text-sm text-brand-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-sm text-brand-muted">Nothing here. Physicians will populate this queue as they hit 👎 or Improve.</div>
        )}
        <div className="space-y-3">
          {items.map((fb) => {
            const isOpen = expanded === fb.feedback_id;
            const state = promoteState[fb.feedback_id] || {};
            const isSafety = CLINICAL_SAFETY_DOMAINS.includes(state.domain);
            return (
              <div
                key={fb.feedback_id}
                className="border border-brand rounded-md bg-white p-3"
                data-testid={`lq-item-${fb.feedback_id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-[11px] mb-1">
                      <span className="px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide bg-slate-100 text-slate-700">
                        {fb.platform_module}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${
                          fb.rating === "up"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                        }`}
                      >
                        {fb.rating === "up" ? "👍 helpful" : "👎 correction"}
                      </span>
                      {fb.confidence_level && (
                        <span className="px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700">
                          confidence: {fb.confidence_level}
                        </span>
                      )}
                      <span className="text-brand-muted ml-auto">
                        {new Date(fb.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-brand-muted mb-1">
                      <b className="text-brand-text">Query:</b> {fb.original_query}
                    </div>
                    <div className="text-xs text-brand-muted mb-1 line-clamp-2">
                      <b className="text-brand-text">AI answered:</b> {fb.original_ai_response}
                    </div>
                    {fb.correction_text && (
                      <div className="text-xs text-brand-text mt-1 border-l-2 border-brand-primary pl-2">
                        <b>Physician correction:</b> {fb.correction_text}
                      </div>
                    )}
                    {Array.isArray(fb.retrieved_entry_ids) && fb.retrieved_entry_ids.length > 0 && (
                      <div className="text-[10px] text-brand-muted mt-1">
                        <BookOpen className="w-3 h-3 inline mr-1" />
                        {fb.retrieved_entry_ids.length} KB entries were used to draft this response
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {activeStatus === "new" && (
                      <>
                        <button
                          onClick={() => (isOpen ? setExpanded(null) : onOpen(fb))}
                          className="px-2.5 py-1.5 rounded-md bg-brand-primary text-white text-xs inline-flex items-center gap-1.5 hover:bg-[#35096B]"
                          data-testid={`lq-promote-open-${fb.feedback_id}`}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {isOpen ? "Close" : "Promote to KB"}
                        </button>
                        <button
                          onClick={() => hold(fb)}
                          disabled={busy}
                          className="px-2.5 py-1.5 rounded-md border border-brand text-xs text-brand-muted hover:bg-brand-bg inline-flex items-center gap-1.5"
                          data-testid={`lq-hold-${fb.feedback_id}`}
                        >
                          <PauseCircle className="w-3.5 h-3.5" /> Hold
                        </button>
                        <button
                          onClick={() => reject(fb)}
                          disabled={busy}
                          className="px-2.5 py-1.5 rounded-md border border-red-200 text-xs text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"
                          data-testid={`lq-reject-${fb.feedback_id}`}
                        >
                          <X className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                    {activeStatus !== "new" && (
                      <span className="text-[10px] text-brand-muted italic self-center">
                        {activeStatus === "promoted" && fb.promoted_entry_id && (
                          <>→ entry {fb.promoted_entry_id.slice(0, 8)}…</>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-3 border-t border-brand pt-3 space-y-3" data-testid={`lq-promote-form-${fb.feedback_id}`}>
                    <div className="flex items-center gap-2 text-xs text-brand-muted">
                      <MessageSquareText className="w-4 h-4 text-brand-primary" />
                      Draft a new KB entry from this correction. Entries will land as{" "}
                      <b>provisional</b> unless verified afterwards.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Domain</label>
                        <select
                          value={state.domain || ""}
                          onChange={(e) => updatePromote(fb.feedback_id, { domain: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm"
                          data-testid={`lq-promote-domain-${fb.feedback_id}`}
                        >
                          {DOMAIN_OPTIONS.map((d) => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Title</label>
                        <input
                          type="text"
                          value={state.title || ""}
                          onChange={(e) => updatePromote(fb.feedback_id, { title: e.target.value })}
                          maxLength={200}
                          className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm"
                          data-testid={`lq-promote-title-${fb.feedback_id}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Content</label>
                      <textarea
                        value={state.content || ""}
                        onChange={(e) => updatePromote(fb.feedback_id, { content: e.target.value })}
                        rows={4}
                        maxLength={2000}
                        className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm resize-y"
                        data-testid={`lq-promote-content-${fb.feedback_id}`}
                      />
                    </div>
                    {isSafety && (
                      <div className="border border-red-300 bg-red-50 rounded-md p-3" data-testid={`lq-verify-block-${fb.feedback_id}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <ShieldAlert className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                          <div className="text-xs text-red-900 leading-relaxed">
                            <b>Clinical-safety domain</b> — entries in <code>{state.domain}</code> can trigger
                            escalations. Type <code>VERIFY</code> (uppercase, exact) to acknowledge you have
                            reviewed this against source-of-truth guidelines. The entry will still land as
                            <b> provisional</b> and needs a second verify pass before it can graduate.
                          </div>
                        </div>
                        <input
                          type="text"
                          value={state.typedVerify || ""}
                          onChange={(e) => updatePromote(fb.feedback_id, { typedVerify: e.target.value })}
                          placeholder="Type VERIFY"
                          className="w-full px-3 py-2 bg-white border border-red-300 rounded-md text-sm font-mono"
                          data-testid={`lq-verify-input-${fb.feedback_id}`}
                        />
                      </div>
                    )}
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setExpanded(null)}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg disabled:opacity-60"
                        data-testid={`lq-promote-cancel-${fb.feedback_id}`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => promote(fb)}
                        disabled={busy || (isSafety && state.typedVerify !== "VERIFY")}
                        className="text-xs px-3 py-1.5 rounded-md bg-brand-primary text-white hover:bg-[#35096B] inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid={`lq-promote-submit-${fb.feedback_id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> {busy ? "Promoting…" : "Promote to KB"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <div className="mt-4 text-[11px] text-brand-muted flex items-center gap-2" data-testid="lq-safety-note">
        <ShieldCheck className="w-3.5 h-3.5" />
        This surface is gated by admin role + TOTP 2FA on both the frontend and{" "}
        <code>require_admin_2fa</code> on the backend.
      </div>
    </>
  );
}
